/**
 * ClosingPageEditor
 *
 * P_SURVEY_CLOSING_PAGE_1.1 — single-language authoring for the
 * post-completion experience. Follows the same "Author Once, Translate
 * Everywhere" pattern used everywhere else in YulOnTap.
 *
 * Fields the admin can configure (all optional; this is opt-in via the
 * top-level `enabled` toggle):
 *   - title           (TranslatedText, auto-translated)
 *   - message         (TranslatedText, auto-translated)
 *   - image_url       (optional, single string)
 *   - action_button   (label TranslatedText + url string, optional)
 *   - redirect_url    (optional auto-redirect after a few seconds)
 *   - google_review_enabled  → toggles a location-aware Google Review CTA
 *
 * When `enabled === false` (the default), the public page falls back
 * to the legacy "Thank you / Thank you description" copy.
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import api from '../../../lib/api';

const NONE = '__none__';

const ClosingPageEditor = ({ closingPage, authoringLang = 'en', organizationId = null, onChange }) => {
    const { t } = useTranslation();
    const cp = closingPage || {};
    const [rewardCards, setRewardCards] = useState([]);

    // P_SURVEY_SCRATCH_WIN_1.0 — cards of THIS organization with the Gift
    // Generator switched on at both card and organization level.
    useEffect(() => {
        let alive = true;
        (async () => {
            if (!organizationId) { setRewardCards([]); return; }
            try {
                const res = await api.get('/cards');
                const candidates = (res.data || []).filter(
                    (c) => c.organization_id === organizationId && c.gift_generator_enabled
                );
                const checked = await Promise.all(candidates.map(async (c) => {
                    try {
                        const cfg = await api.get(`/vouchers/gift/config/${encodeURIComponent(c.card_id)}`);
                        return cfg.data?.org_enabled ? c : null;
                    } catch { return null; }
                }));
                if (alive) setRewardCards(checked.filter(Boolean));
            } catch {
                if (alive) setRewardCards([]);
            }
        })();
        return () => { alive = false; };
    }, [organizationId]);

    const patch = (next) => onChange({ ...cp, ...next });
    const patchTranslated = (key, value) => {
        const current = (cp[key] && typeof cp[key] === 'object') ? cp[key] : {};
        patch({ [key]: { ...current, [authoringLang]: value } });
    };
    const patchAction = (next) => {
        const current = cp.action_button || { label: {}, url: '' };
        patch({ action_button: { ...current, ...next } });
    };
    const patchActionLabel = (value) => {
        const current = cp.action_button || { label: {}, url: '' };
        const label = (current.label && typeof current.label === 'object') ? current.label : {};
        patch({ action_button: { ...current, label: { ...label, [authoringLang]: value } } });
    };

    const langTag = authoringLang === 'fr' ? 'FR' : authoringLang === 'es' ? 'ES' : 'EN';

    // Scratch & Win reward card — ONE dropdown, nothing else. Empty = off.
    // Shown whether or not the closing page itself is enabled, because the
    // reward button also appears on the legacy thank-you screen.
    const rewardField = (
        <div data-testid="closing-page-reward-card-section">
            <Label className="block mb-1.5">
                {t('surveys.closing.rewardCardLabel', 'Scratch & Win reward card')}
            </Label>
            <Select
                value={cp.reward_card_id || NONE}
                onValueChange={(v) => patch({ reward_card_id: v === NONE ? null : v })}
            >
                <SelectTrigger data-testid="closing-page-reward-card-select">
                    <SelectValue placeholder={t('surveys.closing.rewardCardNone', 'No reward')} />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={NONE} data-testid="closing-page-reward-card-none">
                        {t('surveys.closing.rewardCardNone', 'No reward')}
                    </SelectItem>
                    {rewardCards.map((c) => (
                        <SelectItem key={c.card_id} value={c.card_id} data-testid={`closing-page-reward-card-${c.card_id}`}>
                            {c.card_id}{c.name ? ` — ${c.name}` : ''}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
                {t('surveys.closing.rewardCardHelp', 'Respondents who complete this survey get one scratch on the chosen Gift Generator card. Leave empty for no reward.')}
            </p>
        </div>
    );

    // P_SURVEY_GIFT_1.0 — "A gift from us". Reuses the voucher engine; shown
    // on the closing screen whether or not the custom closing page is on.
    const gift = cp.gift || {};
    const patchGift = (next) => patch({ gift: { ...gift, ...next } });
    const patchSpec = (which, next) => {
        const current = gift[which] || {};
        patchGift({ [which]: { ...current, ...next } });
    };
    const specFields = (which, testPrefix) => {
        const spec = gift[which] || {};
        return (
            <div className="space-y-3" data-testid={`${testPrefix}-fields`}>
                <div>
                    <Label className="block mb-1.5">
                        {t('surveys.gift.offerLabel', 'The offer (big text)')}
                    </Label>
                    <Input
                        value={spec.title || ''}
                        onChange={(e) => patchSpec(which, { title: e.target.value })}
                        placeholder={t('surveys.gift.offerPlaceholder', 'e.g. $250 off a $500 treatment')}
                        data-testid={`${testPrefix}-title-input`}
                    />
                </div>
                <div>
                    <Label className="block mb-1.5">
                        {t('surveys.gift.termsLabel', 'Terms line (optional)')}
                    </Label>
                    <Input
                        value={spec.terms || ''}
                        onChange={(e) => patchSpec(which, { terms: e.target.value })}
                        placeholder={t('surveys.gift.termsPlaceholder', 'e.g. One per person, cannot be combined')}
                        data-testid={`${testPrefix}-terms-input`}
                    />
                </div>
                <div className="grid grid-cols-3 gap-3">
                    <div>
                        <Label className="block mb-1.5 text-xs">
                            {t('surveys.gift.valueLabel', 'Value ($, optional)')}
                        </Label>
                        <Input
                            type="number" min="0"
                            value={spec.value ?? 0}
                            onChange={(e) => patchSpec(which, { value: Number(e.target.value) || 0 })}
                            data-testid={`${testPrefix}-value-input`}
                        />
                    </div>
                    <div>
                        <Label className="block mb-1.5 text-xs">
                            {t('surveys.gift.validFromLabel', 'Valid from (days)')}
                        </Label>
                        <Input
                            type="number" min="0"
                            value={spec.valid_from_days ?? 1}
                            onChange={(e) => patchSpec(which, { valid_from_days: Number(e.target.value) || 0 })}
                            data-testid={`${testPrefix}-valid-from-input`}
                        />
                    </div>
                    <div>
                        <Label className="block mb-1.5 text-xs">
                            {t('surveys.gift.expiresLabel', 'Expires (days)')}
                        </Label>
                        <Input
                            type="number" min="1"
                            value={spec.expiry_days ?? 90}
                            onChange={(e) => patchSpec(which, { expiry_days: Number(e.target.value) || 1 })}
                            data-testid={`${testPrefix}-expiry-input`}
                        />
                    </div>
                </div>
            </div>
        );
    };
    const giftField = (
        <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-4" data-testid="closing-page-gift-section">
            <div className="flex items-start gap-3">
                <Switch
                    checked={!!gift.enabled}
                    disabled={!!cp.reward_card_id}
                    onCheckedChange={(v) => patchGift({ enabled: v })}
                    data-testid="closing-page-gift-toggle"
                />
                <div className="space-y-0.5">
                    <p className="text-sm font-medium">
                        {t('surveys.gift.enableLabel', 'Offer “A gift from us”')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {cp.reward_card_id
                            ? t('surveys.gift.scratchConflict', 'Turn off the Scratch & Win reward card first — a survey offers one reward at a time.')
                            : t('surveys.gift.enableHelp', 'After the survey, respondents can claim a gift for themselves, send one to a friend, or both.')}
                    </p>
                </div>
            </div>

            {gift.enabled && (
                <div className="space-y-4">
                    <div>
                        <Label className="block mb-2">
                            {t('surveys.gift.buttonsLabel', 'Which choices to offer')}
                        </Label>
                        <div className="space-y-2">
                            {[
                                ['allow_self', t('surveys.gift.buttonSelf', 'Get my gift')],
                                ['allow_friend', t('surveys.gift.buttonFriend', 'Give a gift to a friend')],
                                ['allow_both', t('surveys.gift.buttonBoth', 'Get one, give one')],
                            ].map(([key, label]) => (
                                <label key={key} className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4"
                                        checked={gift[key] !== false}
                                        onChange={(e) => patchGift({ [key]: e.target.checked })}
                                        data-testid={`closing-page-gift-${key}`}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                        {gift.allow_self === false && gift.allow_friend === false && gift.allow_both === false && (
                            <p className="text-xs text-red-500 mt-1" data-testid="closing-page-gift-no-choice-warning">
                                {t('surveys.gift.atLeastOne', 'Choose at least one.')}
                            </p>
                        )}
                    </div>

                    <div>
                        <p className="text-sm font-semibold mb-2">
                            {t('surveys.gift.respondentHeading', 'Gift for the respondent')}
                        </p>
                        {specFields('respondent_gift', 'closing-page-gift-respondent')}
                    </div>

                    <div>
                        <p className="text-sm font-semibold mb-2">
                            {t('surveys.gift.friendHeading', 'Gift for the friend')}
                        </p>
                        <div className="flex items-center gap-3 mb-3">
                            <Switch
                                checked={gift.friend_same_as_respondent !== false}
                                onCheckedChange={(v) => patchGift({ friend_same_as_respondent: v })}
                                data-testid="closing-page-gift-friend-same-toggle"
                            />
                            <span className="text-sm">
                                {t('surveys.gift.friendSame', "Same gift as the respondent's")}
                            </span>
                        </div>
                        {gift.friend_same_as_respondent === false && specFields('friend_gift', 'closing-page-gift-friend')}
                    </div>

                    <div>
                        <Label className="block mb-1.5">
                            {t('surveys.gift.totalLimitLabel', 'Total gifts this survey may issue')}
                        </Label>
                        <Input
                            type="number" min="1"
                            value={gift.total_limit ?? ''}
                            onChange={(e) => patchGift({ total_limit: Number(e.target.value) || 0 })}
                            placeholder="100"
                            data-testid="closing-page-gift-limit-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1" data-testid="closing-page-gift-issued-so-far">
                            {t('surveys.gift.issuedSoFar', 'Issued so far: {{count}}', { count: gift.issued_count || 0 })}
                            {' · '}
                            {t('surveys.gift.limitHelp', 'Counts every card issued, including gifts sent to friends.')}
                        </p>
                        {!gift.total_limit && (
                            <p className="text-xs text-red-500 mt-1" data-testid="closing-page-gift-limit-warning">
                                {t('surveys.gift.limitRequired', 'Set a total limit.')}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <Card data-testid="closing-page-card">
            <CardHeader>
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <CardTitle className="text-base">
                            {t('surveys.closing.title', 'Closing Page')}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('surveys.closing.help', 'Configure what respondents see after they finish the survey.')}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={!!cp.enabled}
                            onCheckedChange={(v) => patch({ enabled: v })}
                            data-testid="closing-page-enabled-toggle"
                        />
                        <span className="text-xs text-muted-foreground">
                            {cp.enabled
                                ? t('surveys.closing.on', 'Enabled')
                                : t('surveys.closing.off', 'Disabled (uses default Thank You)')}
                        </span>
                    </div>
                </div>
            </CardHeader>

            {cp.enabled && (
                <CardContent className="space-y-5">
                    {/* Title */}
                    <div>
                        <Label className="block mb-1.5">
                            {t('surveys.closing.titleLabel', 'Closing title')}
                            <span className="text-xs text-muted-foreground ml-2 font-normal">({langTag})</span>
                        </Label>
                        <Input
                            value={cp.title?.[authoringLang] || ''}
                            onChange={(e) => patchTranslated('title', e.target.value)}
                            placeholder={t('surveys.closing.titlePlaceholder', 'Thank you for your feedback!')}
                            data-testid="closing-page-title-input"
                        />
                    </div>

                    {/* Message */}
                    <div>
                        <Label className="block mb-1.5">
                            {t('surveys.closing.messageLabel', 'Closing message')}
                            <span className="text-xs text-muted-foreground ml-2 font-normal">({langTag})</span>
                        </Label>
                        <Textarea
                            value={cp.message?.[authoringLang] || ''}
                            onChange={(e) => patchTranslated('message', e.target.value)}
                            placeholder={t('surveys.closing.messagePlaceholder', 'Your input helps us improve every visit.')}
                            rows={3}
                            data-testid="closing-page-message-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('surveys.autoTranslateHint', 'Other languages will be translated automatically after save.')}
                        </p>
                    </div>

                    {/* Optional image */}
                    <div>
                        <Label className="block mb-1.5">
                            {t('surveys.closing.imageUrlLabel', 'Image / icon URL (optional)')}
                        </Label>
                        <Input
                            value={cp.image_url || ''}
                            onChange={(e) => patch({ image_url: e.target.value || null })}
                            placeholder="https://…"
                            data-testid="closing-page-image-url-input"
                        />
                    </div>

                    {/* Action button */}
                    <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 space-y-3">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                            {t('surveys.closing.actionButtonLabel', 'Action button (optional)')}
                        </Label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Input
                                value={cp.action_button?.label?.[authoringLang] || ''}
                                onChange={(e) => patchActionLabel(e.target.value)}
                                placeholder={t('surveys.closing.actionLabelPlaceholder', 'Button label')}
                                data-testid="closing-page-action-label-input"
                            />
                            <Input
                                value={cp.action_button?.url || ''}
                                onChange={(e) => patchAction({ url: e.target.value })}
                                placeholder="https://…"
                                data-testid="closing-page-action-url-input"
                            />
                        </div>
                    </div>

                    {/* Auto-redirect URL */}
                    <div>
                        <Label className="block mb-1.5">
                            {t('surveys.closing.redirectUrlLabel', 'Auto-redirect URL (optional)')}
                        </Label>
                        <Input
                            value={cp.redirect_url || ''}
                            onChange={(e) => patch({ redirect_url: e.target.value || null })}
                            placeholder="https://…"
                            data-testid="closing-page-redirect-url-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                            {t('surveys.closing.redirectHelp', 'If set, the respondent is redirected here a few seconds after the closing page appears.')}
                        </p>
                    </div>

                    {rewardField}

                    {giftField}

                    {/* Google Review CTA */}
                    <div className="flex items-start gap-3 rounded-md border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/40 dark:bg-emerald-950/20 p-3">
                        <Switch
                            checked={!!cp.google_review_enabled}
                            onCheckedChange={(v) => patch({ google_review_enabled: v })}
                            data-testid="closing-page-google-review-toggle"
                        />
                        <div className="space-y-0.5">
                            <p className="text-sm font-medium">
                                {t('surveys.closing.googleReviewLabel', 'Show “Leave us a Google Review” invitation')}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {t('surveys.closing.googleReviewHelp', 'When enabled, the closing page shows a Google Review button using the URL configured for this survey’s location.')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            )}
            {!cp.enabled && (
                <CardContent className="space-y-4">
                    {rewardField}

                    {giftField}
                </CardContent>
            )}
        </Card>
    );
};

export default ClosingPageEditor;
